import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListUnitsResult } from '@whinerp/client';
import { Button, CapabilityUnavailable, ErrorState, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * Units of measure (F-161).
 *
 * A unit is made of another unit, as a person says it - "a case holds 6
 * boxes" - and shown with what it comes to in the base unit, which is what the
 * server stores. The two side by side is how somebody notices that a box was
 * entered as ten when it holds twelve.
 */
export function UnitsPanel(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const measuring = useEntitlement('F-161');
  const [label, setLabel] = useState('');
  const [ofUnitId, setOfUnitId] = useState('');
  const [count, setCount] = useState('');

  const units = useQuery<ListUnitsResult, ApiError>({
    queryKey: ['units'],
    queryFn: () => api.listUnits({}),
    enabled: measuring.enabled,
  });
  const create = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.createUnit({ body: { label, ofUnitId: ofUnitId || null, count: ofUnitId ? count : null } }),
    onSuccess: async () => {
      setLabel('');
      setOfUnitId('');
      setCount('');
      await queryClient.invalidateQueries({ queryKey: ['units'] });
    },
  });

  const heading = (
    <div className="flex flex-col gap-1">
      <SectionLabel>{t('unit.title')}</SectionLabel>
      <p className="text-meta text-ink-meta">{t('unit.body')}</p>
    </div>
  );

  if (measuring.state === 'off' || measuring.state === 'plan-locked') {
    return (
      <Panel data-region="units" id="units">
        <div className="flex flex-col gap-3">
          {heading}
          <CapabilityUnavailable
            kind={measuring.state === 'off' ? 'module-off' : 'plan-locked'}
            capability={t('unit.title')}
            title={t('capability.moduleOff.title')}
            body={t('capability.moduleOff.body', { values: { capability: t('unit.title') } })}
            action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
          />
        </div>
      </Panel>
    );
  }
  if (units.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (units.isError) {
    return <ErrorState title={t('state.error')} body={errorText(units.error)} onRetry={() => void units.refetch()} />;
  }

  const all = units.data.units;
  return (
    <Panel data-region="units" id="units">
      <div className="flex flex-col gap-3">
        {heading}
        {all.length === 0 ? (
          <p data-units-none className="text-meta text-ink-meta">{t('unit.none')}</p>
        ) : (
          <ul className="flex flex-col">
            {all.map((unit) => (
              <li key={unit.unitId} data-unit={unit.unitId} className="flex items-baseline gap-2 border-t border-t-rule py-1 first:border-t-0">
                <span className="min-w-0 flex-1 text-body text-ink">{unit.label}</span>
                <span className="text-meta text-ink-meta">
                  {unit.baseUnitId
                    ? t('unit.comesTo', { values: { factor: unit.factor, base: unit.baseLabel ?? '' } })
                    : t('unit.isBase')}
                </span>
              </li>
            ))}
          </ul>
        )}
        {create.error ? (
          <p data-unit-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {errorText(create.error)}
          </p>
        ) : null}
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-dense text-ink-600">
            {t('unit.name')}
            <input data-unit-label className="h-input rounded-control border border-control bg-surface px-3 text-body text-ink" value={label} onChange={(event) => setLabel(event.target.value)} />
          </label>
          {all.length > 0 ? (
            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('unit.madeOf')}
              <select
                data-unit-of
                className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
                value={ofUnitId}
                onChange={(event) => setOfUnitId(event.target.value)}
              >
                <option value="">{t('unit.aBaseUnit')}</option>
                {all.map((unit) => (
                  <option key={unit.unitId} value={unit.unitId}>
                    {t('unit.holdsSome', { values: { unit: unit.label } })}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {ofUnitId ? (
            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('unit.howMany')}
              <input data-unit-count inputMode="decimal" className="h-input w-col-count rounded-control border border-control bg-surface px-3 text-body text-ink" value={count} onChange={(event) => setCount(event.target.value)} />
            </label>
          ) : null}
          <Button intent="secondary" size="small" data-unit-add disabled={create.isPending} onClick={() => create.mutate()}>
            {t('unit.add')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
